import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Clock,
  MessageSquare,
  X as XIcon,
  Wallet,
  Star,
  Calendar as CalIcon,
  Phone,
} from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { adminGetLead, adminGetSettings, adminPatchLead } from "@/lib/api";
import { API } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const SCOPE_LABELS = {
  sidewalk: "Sidewalk / Walkway",
  deck: "Deck / Patio",
  furniture: "Outdoor Furniture",
  other: "Other",
};
const FREQ_LABELS = {
  one_time: "One-time deep clean",
  weekly: "Weekly",
  biweekly: "Bi-weekly",
  monthly: "Monthly",
};

const STATUS_PILL_LABEL = {
  pending_approval: "Pending",
  approved: "Approved",
  completed: "Completed",
  declined: "Declined",
};

function fmtPhone(p) {
  if (!p || p.length !== 10) return p || "";
  return `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function fmtDay(ymd) {
  if (!ymd) return "";
  return new Date(`${ymd}T00:00:00`).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

// Countdown component: available at completedAt + 24h.
function ReviewCountdown({ completedAt, children }) {
  const target = useMemo(
    () => (completedAt ? new Date(completedAt).getTime() + 24 * 3600 * 1000 : 0),
    [completedAt]
  );
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  const available = now >= target;
  if (available) return children(true, "");
  const msLeft = target - now;
  const h = Math.floor(msLeft / 3600_000);
  const m = Math.floor((msLeft % 3600_000) / 60_000);
  return children(false, `${h}h ${m}m`);
}

const PHONE_TEXT_LINE = "646-657-5213";

// Payment handles + Venmo profile — hardcoded per owner (Phase 3).
// Consolidated so a future admin-editable move is a one-file change.
const PAY_HANDLES = {
  venmoProfile: "Pryncecharlie",
  cashApp: "$pryncecharliechime",
  chime: "$Pryncecharlie",
};

function paymentSmsBody(firstName, finalAmount) {
  // Owner spec: integer dollars in all three [AMOUNT] slots; the Venmo URL
  // amount= param is the integer only (no $ sign).
  const n = Number(finalAmount) || 0;
  const amtInt = Math.round(n);
  const amt = `$${amtInt}`;
  const venmoUrl = `https://venmo.com/${PAY_HANDLES.venmoProfile}?amount=${amtInt}&txn=pay&note=PowerWash`;
  return (
    `All done! Your outdoor space is looking brand new. The final total is ${amt}.\n\n` +
    `Tap to pay via Venmo: ${venmoUrl}\n\n` +
    `Prefer Cash App or Chime? Send ${amt} to:\n` +
    `• Cash App: ${PAY_HANDLES.cashApp}\n` +
    `• Chime: ${PAY_HANDLES.chime}\n\n` +
    `Thanks for choosing Green Services! Text me at ${PHONE_TEXT_LINE}.`
  );
}

function smsHref(phone, body) {
  return `sms:${phone}?body=${encodeURIComponent(body)}`;
}

function photoSrc(leadId, photoId) {
  return `${API}/admin/leads/${leadId}/photos/${photoId}`;
}

export default function LeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [finalAmount, setFinalAmount] = useState("");
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  async function reload() {
    try {
      const [l, s] = await Promise.all([adminGetLead(id), adminGetSettings()]);
      setLead(l);
      setSettings(s);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("Lead not found");
        nav("/admin", { replace: true });
      } else {
        toast.error("Could not load lead");
      }
    }
  }

  useEffect(() => {
    reload();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!lead) {
    return (
      <AdminShell>
        <div className="py-16 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#16A34A] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminShell>
    );
  }

  async function doPatch(body, okMsg) {
    setBusy(true);
    try {
      const updated = await adminPatchLead(id, body);
      setLead(updated);
      toast.success(okMsg);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Update failed");
    } finally {
      setBusy(false);
    }
  }

  async function onApprove() {
    if (busy) return;
    await doPatch({ status: "approved" }, "Approved");
  }
  async function onDeclineConfirm() {
    if (busy) return;
    await doPatch(
      { status: "declined", decline_reason: declineReason || null },
      "Declined"
    );
    setDeclineOpen(false);
    setDeclineReason("");
  }
  async function onCompleteConfirm() {
    const raw = String(finalAmount).trim();
    const n = Number(raw);
    if (!raw || !Number.isFinite(n) || n <= 0) {
      toast.error("Enter a valid amount greater than $0");
      return;
    }
    await doPatch({ status: "completed", final_amount: n }, "Marked complete");
    setCompleteOpen(false);
    setFinalAmount("");
  }

  // sms bodies (owner-provided copy)
  const firstName = (lead.customer?.name || "").split(" ")[0] || lead.customer?.name || "";
  const dayLabel = fmtDay(lead.booking?.preferred_date);
  const windowLabel = lead.booking?.preferred_window === "AM" ? "AM" : "PM";
  const confirmBody =
    `Hi ${firstName}, it's Charlie from Green Services. Confirming your appointment on ${dayLabel} in the ${windowLabel} window. See you then! - Reply STOP to opt out.`;

  const paymentBody = paymentSmsBody(firstName, lead.final_amount ?? 0);

  const googleReviewUrl =
    settings?.google_review_url?.trim() || "https://google.com/review-placeholder";
  const reviewBody = `Hey ${firstName}, thanks again for choosing Green Services! Would you mind leaving us a quick review? ${googleReviewUrl} — Charlie`;

  const status = lead.status;

  return (
    <AdminShell>
      <div className="pt-2">
        <Link
          to="/admin"
          data-testid="detail-back"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#334E68]"
        >
          <ArrowLeft className="w-4 h-4" /> Back to leads
        </Link>

        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#334E68]">
            {lead.customer?.name}
          </h1>
          <span
            data-testid="detail-status-pill"
            className="text-xs px-2.5 py-1 rounded-full border font-medium bg-white text-[#334E68] border-gray-300"
          >
            {STATUS_PILL_LABEL[status] || status}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-0.5">
          Submitted {fmtDate(lead.created_at)}
        </p>

        {lead.flags?.non_hoboken_zip && (
          <div
            data-testid="non-hoboken-banner"
            className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-2"
          >
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              <strong>Non-Hoboken zip.</strong> Zip {lead.customer?.zip} is outside
              Hoboken (07030).
            </span>
          </div>
        )}

        {/* Photos */}
        <section className="mt-6">
          <h2 className="text-xs uppercase tracking-wider font-semibold text-gray-500">
            Photos ({lead.photos?.length || 0})
          </h2>
          <div
            data-testid="lead-photos-grid"
            className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3"
          >
            {(lead.photos || []).map((p, i) => {
              const src = photoSrc(lead.id, p.id);
              return (
                <button
                  key={p.id || i}
                  type="button"
                  data-testid={`lead-photo-${i}`}
                  onClick={() => setLightbox(src)}
                  className="aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-100 hover:opacity-90"
                >
                  <img
                    src={src}
                    alt={p.filename}
                    className="w-full h-full object-cover"
                  />
                </button>
              );
            })}
          </div>
        </section>

        {/* Contact + Job details */}
        <section className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
              Contact
            </div>
            <div className="mt-2 space-y-1.5 text-sm text-[#334E68]">
              <div data-testid="detail-address">
                <span className="text-gray-500">Address: </span>
                {lead.customer?.address}, {lead.customer?.zip}
              </div>
              <div>
                <span className="text-gray-500">Phone: </span>
                <a
                  href={`sms:${lead.customer?.phone}`}
                  data-testid="detail-phone"
                  className="text-[#16A34A] font-medium"
                >
                  {fmtPhone(lead.customer?.phone)}
                </a>
              </div>
              {lead.customer?.notes && (
                <div>
                  <span className="text-gray-500">Notes: </span>
                  <span className="whitespace-pre-wrap">
                    {lead.customer.notes}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
              Job
            </div>
            <div className="mt-2 space-y-1.5 text-sm text-[#334E68]">
              <div>
                <span className="text-gray-500">Scope: </span>
                {Object.entries(SCOPE_LABELS)
                  .filter(([k]) => lead.scope?.[k])
                  .map(([k, v]) =>
                    k === "other" && lead.scope?.other_text
                      ? `${v} (${lead.scope.other_text})`
                      : v
                  )
                  .join(", ") || "—"}
              </div>
              <div>
                <span className="text-gray-500">Frequency: </span>
                {FREQ_LABELS[lead.frequency] || lead.frequency}
              </div>
              <div>
                <span className="text-gray-500">Preferred: </span>
                {fmtDay(lead.booking?.preferred_date)} ({windowLabel})
              </div>
              <div>
                <span className="text-gray-500">Estimate: </span>
                <span className="font-semibold">
                  ${lead.estimate?.low}–${lead.estimate?.high}
                </span>
                <span className="text-xs text-gray-500 ml-1">
                  ({lead.estimate?.hours}h @ ${lead.estimate?.hourly_rate}/h)
                </span>
              </div>
              {lead.final_amount != null && (
                <div data-testid="detail-final-amount">
                  <span className="text-gray-500">Final: </span>
                  <span className="font-semibold text-[#16A34A]">
                    ${lead.final_amount.toFixed(2)}
                  </span>
                </div>
              )}
              {lead.decline_reason && (
                <div data-testid="detail-decline-reason">
                  <span className="text-gray-500">Decline reason: </span>
                  {lead.decline_reason}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Marketing source */}
        <section
          data-testid="marketing-source-card"
          className="mt-6 bg-white rounded-2xl border border-gray-200 p-4"
        >
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
            Marketing Source
          </div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-[#334E68]">
            <div>
              <span className="text-gray-500">Ref: </span>
              <span data-testid="source-ref" className="font-medium">
                {lead.source?.ref || "direct"}
              </span>
            </div>
            <div>
              <span className="text-gray-500">utm_source: </span>
              <span data-testid="source-utm-source">
                {lead.source?.utm_source || "—"}
              </span>
            </div>
            <div>
              <span className="text-gray-500">utm_medium: </span>
              <span data-testid="source-utm-medium">
                {lead.source?.utm_medium || "—"}
              </span>
            </div>
            <div>
              <span className="text-gray-500">utm_campaign: </span>
              <span data-testid="source-utm-campaign">
                {lead.source?.utm_campaign || "—"}
              </span>
            </div>
            <div className="sm:col-span-2">
              <span className="text-gray-500">First seen: </span>
              <span data-testid="source-first-seen">
                {lead.source?.first_seen_at || "—"}
              </span>
            </div>
          </div>
        </section>

        {/* Status actions */}
        <section className="mt-6 space-y-3">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
            Actions
          </div>

          {status === "pending_approval" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="btn-approve"
                onClick={onApprove}
                disabled={busy}
                className="min-h-[52px] rounded-2xl bg-[#16A34A] hover:bg-[#15803D] disabled:bg-gray-300 text-white font-semibold text-sm uppercase tracking-wide flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" /> Approve
              </button>
              <button
                type="button"
                data-testid="btn-decline"
                onClick={() => setDeclineOpen(true)}
                disabled={busy}
                className="min-h-[52px] rounded-2xl bg-white border border-gray-200 hover:border-gray-300 disabled:opacity-40 text-[#334E68] font-semibold text-sm uppercase tracking-wide flex items-center justify-center gap-2"
              >
                <XIcon className="w-4 h-4" /> Decline
              </button>
            </div>
          )}

          {status === "approved" && (
            <>
              <a
                href={smsHref(lead.customer?.phone, confirmBody)}
                data-testid="btn-text-confirm"
                className="w-full min-h-[52px] rounded-2xl bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold text-sm uppercase tracking-wide flex items-center justify-center gap-2"
              >
                <MessageSquare className="w-4 h-4" /> Text Customer to Confirm
              </a>
              <button
                type="button"
                data-testid="btn-open-complete"
                onClick={() => setCompleteOpen(true)}
                className="w-full min-h-[52px] rounded-2xl bg-white border border-gray-200 hover:border-gray-300 text-[#334E68] font-semibold text-sm uppercase tracking-wide flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" /> Mark Complete
              </button>
            </>
          )}

          {status === "completed" && (
            <div className="space-y-3">
              <a
                href={smsHref(lead.customer?.phone, paymentBody)}
                data-testid="btn-send-payment"
                className="w-full min-h-[52px] rounded-2xl bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold text-sm uppercase tracking-wide flex items-center justify-center gap-2"
              >
                <Wallet className="w-4 h-4" /> Send Payment Link
              </a>

              {!settings?.google_review_url && (
                <div
                  data-testid="google-review-warning"
                  className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2"
                >
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>
                    Set your Google review URL in{" "}
                    <Link to="/admin/settings" className="underline font-medium">
                      Settings
                    </Link>{" "}
                    — using a placeholder link.
                  </span>
                </div>
              )}

              <ReviewCountdown completedAt={lead.completed_at}>
                {(available, remaining) =>
                  available ? (
                    <a
                      href={smsHref(lead.customer?.phone, reviewBody)}
                      data-testid="btn-send-review"
                      className="w-full min-h-[52px] rounded-2xl bg-white border border-gray-200 hover:border-gray-300 text-[#334E68] font-semibold text-sm uppercase tracking-wide flex items-center justify-center gap-2"
                    >
                      <Star className="w-4 h-4" /> Send Review Request
                    </a>
                  ) : (
                    <button
                      type="button"
                      disabled
                      data-testid="btn-send-review-disabled"
                      className="w-full min-h-[52px] rounded-2xl bg-gray-100 text-gray-500 font-semibold text-sm uppercase tracking-wide flex items-center justify-center gap-2 cursor-not-allowed"
                    >
                      <Clock className="w-4 h-4" /> Review available in{" "}
                      <span data-testid="review-countdown">{remaining}</span>
                    </button>
                  )
                }
              </ReviewCountdown>
            </div>
          )}

          {status === "declined" && (
            <p className="text-sm text-gray-500 italic">
              This lead was declined. No further actions.
            </p>
          )}
        </section>
      </div>

      {/* Complete dialog */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent data-testid="complete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#334E68]">
              Mark job complete
            </DialogTitle>
            <DialogDescription>
              Enter the final amount you charged. This will be used to prefill the
              Venmo payment link.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <Label htmlFor="final_amount" className="text-sm text-[#334E68]">
              Final amount ($)
            </Label>
            <Input
              id="final_amount"
              data-testid="input-final-amount"
              inputMode="decimal"
              value={finalAmount}
              onChange={(e) => setFinalAmount(e.target.value)}
              placeholder="e.g., 250"
              className="mt-2 min-h-[52px] rounded-xl"
              autoFocus
            />
          </div>
          <DialogFooter className="mt-4">
            <button
              type="button"
              onClick={() => setCompleteOpen(false)}
              className="min-h-[44px] px-4 rounded-xl text-sm text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="btn-complete-confirm"
              onClick={onCompleteConfirm}
              disabled={busy}
              className="min-h-[44px] px-6 rounded-xl bg-[#16A34A] hover:bg-[#15803D] disabled:bg-gray-300 text-white text-sm font-semibold uppercase tracking-wide"
            >
              Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline dialog */}
      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent data-testid="decline-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#334E68]">
              Decline this request
            </DialogTitle>
            <DialogDescription>
              Optional — for your own notes. Not sent to the customer.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <Label htmlFor="decline_reason" className="text-sm text-[#334E68]">
              Reason (optional)
            </Label>
            <Textarea
              id="decline_reason"
              data-testid="input-decline-reason"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Booked, out of service area, etc."
              rows={3}
              className="mt-2 rounded-xl"
            />
          </div>
          <DialogFooter className="mt-4">
            <button
              type="button"
              onClick={() => setDeclineOpen(false)}
              className="min-h-[44px] px-4 rounded-xl text-sm text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="btn-decline-confirm"
              onClick={onDeclineConfirm}
              disabled={busy}
              className="min-h-[44px] px-6 rounded-xl bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white text-sm font-semibold uppercase tracking-wide"
            >
              Decline
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      {lightbox && (
        <div
          data-testid="photo-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <img
            src={lightbox}
            alt="Zoomed"
            className="max-w-full max-h-full rounded-xl"
          />
          <button
            type="button"
            aria-label="Close"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox(null);
            }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>
      )}
    </AdminShell>
  );
}
